import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

import { isEntitled } from '@emr/shared';

import { logout } from '../api/auth.ts';
import { SESSION_QUERY_KEY, useGrants, useSession } from '../auth/sessionContext.ts';
import { HelpChatWidget } from '../components/HelpChatWidget.tsx';
import { LogoLockup } from '../components/Logo.tsx';
import { Button } from '../components/primitives.tsx';
import {
  SpotlightProvider,
  useSpotlightTargetRevealer,
  type TargetRevealer,
} from '../components/Spotlight.tsx';
import { DateTimeFormatMenu } from '../format/DateTimeFormatMenu.tsx';
import { useIsDesktop } from '../hooks/useMediaQuery.ts';
import { useUnassignedInboxCount } from '../inbox/useUnassignedInboxCount.ts';
import { LanguageMenu } from '../i18n/LanguageMenu.tsx';
import { useSyncAccountLocale } from '../i18n/useSyncAccountLocale.ts';
import { ProfilePhotoAvatar } from '../profile/ProfilePhotoAvatar.tsx';
import { useOwnPhoto } from '../profile/useOwnPhoto.ts';
import { ThemeMenu, ThemeRejectionNotice } from '../theme/ThemeMenu.tsx';
import { useTheme } from '../theme/themeContext.ts';
import { type ResolvedNavItem, visibleNavItems } from './navigation.ts';

/** `data-help-id` for one nav item, derived from its own path — free instrumentation for
 * `components/Spotlight.tsx` on every current and future nav item, per `BACKLOG.md` B6a's
 * requirement that the highlighting mechanism be general and reusable rather than built ad hoc per
 * feature. `web/src/help/spotlightTargets.ts` is the one place a help topic opts into pointing at
 * one of these. */
function navHelpId(to: string): string {
  return `nav-${to === '/' ? 'overview' : to.replace(/^\//, '')}`;
}

/**
 * The nav badge substituting for staff-side email/push notifications in v1 (`docs/decisions/
 * patient-portal-messaging-scope.md`'s "Notification" section — "the live unread/unassigned count on
 * the inbox nav entry is the actual mechanism that keeps a new general-inbox message from going
 * unseen"). Mounted only beside the `/inbox` `NavLink` itself, never rendered (so its query never
 * fires) for a viewer whose nav does not include that item at all — `NavItemList`'s own `items` prop
 * has already excluded `/inbox` for anyone lacking `patient:read`, and this component has no gate of
 * its own to duplicate that decision.
 *
 * Renders nothing while loading, on error, or at zero — a badge that appears only when there is
 * something to act on, matching `shell/navigation.ts`'s "upgrade" pill's own restraint about not
 * adding noise to every page load.
 */
function InboxUnassignedBadge() {
  const { t } = useTranslation('shell');
  const count = useUnassignedInboxCount();
  if (count.data === undefined || count.data === 0) return null;

  return (
    <span
      className="rounded-pill bg-accent px-2 py-0.5 text-xs font-semibold text-accent-ink"
      aria-label={t('nav.inboxUnassignedBadge', { count: count.data })}
    >
      {count.data}
    </span>
  );
}

/**
 * The authenticated shell: header, navigation, content.
 *
 * **Navigation is conditionally mounted, never CSS-toggled.** Exactly one nav exists in the
 * document at any width. A CSS-toggled dual-DOM approach — rendering both a mobile and a desktop
 * nav at once and hiding one with `display:none` — is a measured hazard, not a hypothetical one:
 * it produces duplicate `id`s (a `<label for>` binding to the hidden copy, leaving the visible
 * control with no accessible name) and Playwright strict-mode failures from text appearing twice.
 * A CSS class cannot cause either of those; a second copy in the DOM causes both.
 */
export function AppShell() {
  const { t } = useTranslation('shell');
  const session = useSession();
  const grants = useGrants();
  const isDesktop = useIsDesktop();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  const { orgBranding } = useTheme();

  useSyncAccountLocale();

  const items = visibleNavItems({ grants, tier: session.tier });
  // Full logo replacement is `whiteLabel`-gated (`BACKLOG.md` B4) — a lower tier's practice may still
  // *set* `logoUrl` in storage (nothing about `orgs.logoUrl` itself is tier-aware) but never rendered
  // for it, matching `entitlements.ts`'s "resolve at read time, every time" rule the same way
  // `navigation.ts`'s own `isEntitled` calls do; a downgrade takes effect on the very next render
  // with no separate cleanup step needed.
  const orgLogoUrl = isEntitled(session.tier, 'whiteLabel') ? (orgBranding?.logoUrl ?? null) : null;

  const openMobileNav = useCallback(() => {
    setMobileNavOpen(true);
  }, [setMobileNavOpen]);

  return (
    <SpotlightProvider>
      <MobileNavSpotlightRevealer
        items={items}
        isDesktop={isDesktop}
        mobileNavOpen={mobileNavOpen}
        openMobileNav={openMobileNav}
      />
      <div className="flex flex-1 flex-col bg-canvas">
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-control focus:bg-surface focus:px-3 focus:py-2 focus:text-ink"
        >
          {t('menu.skipToContent')}
        </a>

        <header className="border-b border-line bg-surface">
          <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-3 px-4 py-3">
            {!isDesktop ? (
              <Button
                variant="secondary"
                aria-expanded={mobileNavOpen}
                aria-controls="mobile-nav"
                onClick={() => {
                  setMobileNavOpen((open) => !open);
                }}
              >
                {mobileNavOpen ? t('menu.close') : t('menu.open')}
              </Button>
            ) : null}

            <LogoLockup size={24} orgLogoUrl={orgLogoUrl} />

            <span className="min-w-0 flex-1 truncate text-sm text-ink-muted">
              {session.orgName}
            </span>

            <OwnPhotoLink />
            <DateTimeFormatMenu />
            <ThemeMenu />
            <LanguageMenu />
            <SignOutButton />
          </div>

          {/* Mounted only below the desktop breakpoint, and only while open. */}
          {!isDesktop && mobileNavOpen ? (
            <nav
              id="mobile-nav"
              aria-label="Primary"
              className="border-t border-line px-4 pt-2 pb-4"
            >
              <NavItemList
                items={items}
                currentPath={location.pathname}
                onNavigate={() => {
                  setMobileNavOpen(false);
                }}
              />
            </nav>
          ) : null}
        </header>

        <div className="mx-auto flex w-full max-w-6xl flex-1 gap-6 px-4 py-4 sm:py-6">
          {isDesktop ? (
            <nav aria-label="Primary" className="w-56 shrink-0">
              <NavItemList items={items} currentPath={location.pathname} />
            </nav>
          ) : null}

          <main id="main" className="min-w-0 flex-1">
            <ThemeRejectionNotice />
            <Outlet />
          </main>
        </div>

        <HelpChatWidget />
      </div>
    </SpotlightProvider>
  );
}

/**
 * Registers a `TargetRevealer` (`components/Spotlight.tsx`) that opens the mobile nav drawer when
 * the requested spotlight target is one of this shell's own nav items and the drawer is currently
 * collapsed — the fix for the bug that file's own header comment describes: at phone width, with
 * the hamburger menu closed (the default state), a nav-mapped "Show me" reported "that control
 * isn't on this page" even though it was one tap away, because `<nav id="mobile-nav">` only mounts
 * while the drawer is open.
 *
 * Renders nothing. It exists purely to sit inside `SpotlightProvider`'s own subtree — `AppShell`
 * itself, one level higher, cannot call `useSpotlightTargetRevealer` directly, since that hook needs
 * the context value `SpotlightProvider` only supplies to its own descendants, and `AppShell` is
 * `SpotlightProvider`'s parent, not its child, in the tree it returns.
 */
function MobileNavSpotlightRevealer({
  items,
  isDesktop,
  mobileNavOpen,
  openMobileNav,
}: {
  readonly items: readonly ResolvedNavItem[];
  readonly isDesktop: boolean;
  readonly mobileNavOpen: boolean;
  readonly openMobileNav: () => void;
}) {
  const navHelpIds = useMemo(() => new Set(items.map(({ item }) => navHelpId(item.to))), [items]);

  const revealer = useCallback<TargetRevealer>(
    (targetId) => {
      // Already visible on desktop, or already open on mobile — nothing for this revealer to do;
      // returning `false` lets `Spotlight.tsx`'s `show()` report accurately if no other registered
      // revealer claims the id either.
      if (isDesktop || mobileNavOpen) return false;
      if (!navHelpIds.has(targetId)) return false;
      openMobileNav();
      return true;
    },
    [isDesktop, mobileNavOpen, navHelpIds, openMobileNav],
  );

  useSpotlightTargetRevealer(revealer);

  return null;
}

function NavItemList({
  items,
  currentPath,
  onNavigate,
}: {
  readonly items: readonly ResolvedNavItem[];
  readonly currentPath: string;
  readonly onNavigate?: () => void;
}) {
  const { t } = useTranslation('shell');

  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0">
      {items.map(({ item, availability }) => (
        <li key={item.to}>
          {availability.kind === 'upgrade' ? (
            // Visible and disabled, not hidden. `shared/src/entitlements.ts` is explicit that an
            // entitlement failure is upgrade-shaped and a permission failure is not; hiding this
            // would make the feature undiscoverable to the person who would buy it.
            <span
              className="flex items-center justify-between gap-2 rounded-control px-3 py-2 text-sm text-ink-muted"
              aria-disabled="true"
            >
              {t(item.labelKey)}
              <span className="rounded-pill border border-line px-2 py-0.5 text-xs">
                {t('nav.upgrade')}
              </span>
            </span>
          ) : (
            <NavLink
              to={item.to}
              end={item.to === '/'}
              onClick={onNavigate}
              aria-current={currentPath === item.to ? 'page' : undefined}
              data-help-id={navHelpId(item.to)}
              className={({ isActive }) =>
                [
                  'flex items-center justify-between gap-2 rounded-control px-3 py-2 text-sm font-medium',
                  isActive ? 'bg-nav-active text-nav-active-ink' : 'text-ink hover:bg-raised',
                ].join(' ')
              }
            >
              {t(item.labelKey)}
              {item.to === '/inbox' ? <InboxUnassignedBadge /> : null}
            </NavLink>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * The caller's own photo (`BACKLOG.md` A12), linking to `/signature` — the closest existing
 * self-service profile page, where the actual upload/replace/remove control lives
 * (`SignaturePage.tsx`'s own "Profile photo" panel). Renders the initial-letter placeholder while
 * loading or when there is no photo on file, never a spinner or nothing at all — this sits in the
 * header on every page, so a slow or empty state must not shift the layout around it.
 */
function OwnPhotoLink() {
  const { t } = useTranslation('shell');
  const session = useSession();
  const photo = useOwnPhoto();

  return (
    <Link
      to="/signature"
      aria-label={t('ownPhoto.ariaLabel')}
      className="rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
    >
      <ProfilePhotoAvatar photoUrl={photo.data?.photoUrl ?? null} label={session.email} size={28} />
    </Link>
  );
}

function SignOutButton() {
  const { t } = useTranslation('shell');
  const queryClient = useQueryClient();
  const signOut = useMutation({
    mutationFn: logout,
    // Settled, not success: a logout that errored still means this browser should stop acting
    // signed in. Clearing only on success leaves a user looking at a session the server has
    // already destroyed.
    onSettled: () => {
      void queryClient.resetQueries({ queryKey: SESSION_QUERY_KEY });
    },
  });

  return (
    <Button
      variant="secondary"
      pending={signOut.isPending}
      data-help-id="sign-out-button"
      onClick={() => {
        signOut.mutate();
      }}
    >
      {signOut.isPending ? t('signOut.pending') : t('signOut.action')}
    </Button>
  );
}
